"use client";

import React, { useState, useRef, useEffect } from "react";
import { Send, Mic, Square, Loader2 } from "lucide-react";
import type { Language } from "@/types";

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  onStartRecording: () => void;
  onStopRecording: () => void;
  isLoading: boolean;
  isRecording: boolean;
  isProcessing: boolean;
  language: Language;
  onLanguageChange: (language: Language) => void;
  disabled?: boolean;
}

export function ChatInput({
  onSendMessage,
  onStartRecording,
  onStopRecording,
  isLoading,
  isRecording,
  isProcessing,
  language,
  onLanguageChange,
  disabled = false,
}: ChatInputProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`;
    }
  }, [input]);

  const handleSubmit = () => {
    if (input.trim() && !isLoading && !disabled) {
      onSendMessage(input.trim());
      setInput("");
      if (inputRef.current) {
        inputRef.current.style.height = "auto";
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const isDisabled = disabled || isLoading || isRecording || isProcessing;

  return (
    <div className="bg-white border-t border-gray-200 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Language Selector */}
        <div className="flex justify-center gap-2 mb-3">
          {(["ar", "en", "auto"] as const).map((lang) => (
            <button
              key={lang}
              onClick={() => onLanguageChange(lang)}
              className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                language === lang
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {lang === "ar" ? "العربية" : lang === "en" ? "English" : "Auto"}
            </button>
          ))}
        </div>

        {/* Input Area */}
        <div className="flex items-end gap-3 bg-gray-50 border border-gray-200 rounded-2xl px-4 py-3 focus-within:border-blue-400 focus-within:ring-2 focus-within:ring-blue-100 transition-all">
          {/* Voice Recording Button */}
          <button
            onClick={isRecording ? onStopRecording : onStartRecording}
            disabled={isLoading}
            className="p-2 rounded-full transition-all duration-300 shrink-0"
            style={{
              backgroundColor: isRecording ? "#dc2626" : isProcessing ? "#d3a035" : "#0b015d",
              color: "white",
              opacity: isLoading ? 0.5 : 1,
              animation: isRecording ? "pulse 1.5s infinite" : "none",
            }}
            title={isRecording ? "إيقاف التسجيل" : "بدء التسجيل الصوتي"}
          >
            {isRecording ? (
              <Square size={18} />
            ) : isProcessing ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Mic size={18} />
            )}
          </button>

          {/* Text Input */}
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isRecording
                ? "جاري التسجيل... تحدث الآن"
                : isProcessing
                ? "جاري معالجة الصوت..."
                : isLoading
                ? "MDARJ AI يفكر..."
                : "اكتب رسالتك أو استخدم التسجيل الصوتي..."
            }
            className="flex-1 bg-transparent resize-none focus:outline-none text-gray-700 placeholder-gray-400 max-h-36 min-h-[24px]"
            rows={1}
            disabled={isDisabled}
            dir="rtl"
          />

          {/* Send Button */}
          <button
            onClick={handleSubmit}
            disabled={!input.trim() || isDisabled}
            className="p-2 rounded-full transition-all shrink-0"
            style={{
              backgroundColor: input.trim() && !isDisabled ? "#d3a035" : "#e5e7eb",
              color: input.trim() && !isDisabled ? "white" : "#9ca3af",
              cursor: input.trim() && !isDisabled ? "pointer" : "not-allowed",
            }}
            title="إرسال الرسالة"
          >
            {isLoading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : (
              <Send size={18} />
            )}
          </button>
        </div>

        {/* Recording indicator */}
        {isRecording && (
          <div className="flex items-center justify-center gap-2 mt-2 text-red-500 text-sm" dir="rtl">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            جاري التسجيل... تحدث بوضوح، يتوقف بعد ثانيتين من الصمت
          </div>
        )}
      </div>
    </div>
  );
}
